import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../lib/axios';
import { useProjectStore } from '../store/projectStore';
import { useProject } from '../hooks/useProjects';
import type { WbsTreeNode, InlineCreateState } from '../components/wbs/types';
import { flattenWbsTree, dateToX, getDateRange } from '../components/wbs/wbsUtils';
import WbsHeader from '../components/wbs/WbsHeader';
import WbsBody from '../components/wbs/WbsBody';
import type { ApiResponse } from '@maios/shared';

const DAY_WIDTH: Record<string, number> = { day: 32, week: 12, month: 4 };

export default function SchedulePage() {
  const currentProject = useProjectStore((s) => s.currentProject);
  const projectId = currentProject?.id ?? '';
  const { data: project } = useProject(projectId);

  const [wbsData, setWbsData] = useState<WbsTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeUnit, setTimeUnit] = useState<'day' | 'week' | 'month'>('day');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [bufferEnabled, setBufferEnabled] = useState(true);
  const [inlineCreate, setInlineCreate] = useState<InlineCreateState | null>(null);

  const ganttScrollRef = useRef<HTMLDivElement>(null);
  const members = project?.members ?? [];

  // -----------------------------------------------------------------------
  // 데이터 로드
  // -----------------------------------------------------------------------

  const fetchWbsData = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get<ApiResponse<WbsTreeNode[]>>(`/projects/${projectId}/wbs`);
      const data = res.data.data ?? [];
      setWbsData(data);
      // 최초: 모든 항목 펼침 (3depth까지 모두 표시)
      setExpandedIds((prev) => {
        if (prev.size > 0) return prev;
        const ids = new Set<string>();
        function collectIds(nodes: WbsTreeNode[]) {
          for (const n of nodes) {
            ids.add(n.wbsItemId);
            if (n.children?.length) collectIds(n.children);
          }
        }
        collectIds(data);
        return ids;
      });
    } catch {
      toast.error('WBS 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchWbsSettings = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get<ApiResponse<{ bufferAutoCalc: boolean }>>(`/projects/${projectId}/wbs/settings`);
      setBufferEnabled(res.data.data?.bufferAutoCalc !== false);
    } catch {
      // 기본값 유지
    }
  }, [projectId]);

  useEffect(() => {
    fetchWbsData();
    fetchWbsSettings();
  }, [fetchWbsData, fetchWbsSettings]);

  // -----------------------------------------------------------------------
  // 평탄화
  // -----------------------------------------------------------------------

  const flattenedRows = useMemo(
    () => flattenWbsTree(wbsData, expandedIds),
    [wbsData, expandedIds],
  );

  // -----------------------------------------------------------------------
  // 핸들러
  // -----------------------------------------------------------------------

  const handleToggle = useCallback((wbsItemId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(wbsItemId)) next.delete(wbsItemId);
      else next.add(wbsItemId);
      return next;
    });
  }, []);

  const handleFieldUpdate = useCallback(async (wbsItemId: string, field: string, value: unknown) => {
    try {
      await api.patch(`/projects/${projectId}/wbs/${wbsItemId}`, { [field]: value });
      await fetchWbsData();
    } catch {
      toast.error('수정에 실패했습니다.');
    }
  }, [projectId, fetchWbsData]);

  const handleStartInlineCreate = useCallback((parentId: string | null, depth: number) => {
    // 부모가 있으면 펼침
    if (parentId) {
      setExpandedIds((prev) => new Set(prev).add(parentId));
    }
    setInlineCreate({ parentId, depth });
  }, []);

  const handleInlineSubmit = useCallback(async (data: { parentId: string | null; title: string }) => {
    try {
      await api.post(`/projects/${projectId}/wbs`, {
        parentId: data.parentId,
        title: data.title,
      });
      setInlineCreate(null);
      await fetchWbsData();
    } catch {
      toast.error('항목 생성에 실패했습니다.');
    }
  }, [projectId, fetchWbsData]);

  const handleDelete = useCallback(async (wbsItemId: string, title: string, childCount: number) => {
    const msg = childCount > 0
      ? `"${title}" 및 하위 ${childCount}개 항목도 함께 삭제됩니다. 삭제하시겠습니까?`
      : `"${title}"을(를) 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;

    try {
      await api.delete(`/projects/${projectId}/wbs/${wbsItemId}`);
      await fetchWbsData();
      toast.success('삭제되었습니다.');
    } catch {
      toast.error('삭제에 실패했습니다.');
    }
  }, [projectId, fetchWbsData]);

  const handleBufferToggle = useCallback(async () => {
    const newValue = !bufferEnabled;
    setBufferEnabled(newValue);
    try {
      await api.put(`/projects/${projectId}/wbs/settings`, { bufferAutoCalc: newValue });
      await fetchWbsData();
    } catch {
      setBufferEnabled(!newValue); // 롤백
      toast.error('설정 변경에 실패했습니다.');
    }
  }, [bufferEnabled, projectId, fetchWbsData]);

  const handleScrollToToday = useCallback(() => {
    if (!ganttScrollRef.current) return;
    const range = getDateRange(wbsData);
    if (!range) return;
    const dayWidth = DAY_WIDTH[timeUnit];
    const todayX = dateToX(new Date(), range.minDate, dayWidth);
    ganttScrollRef.current.scrollTo({ left: todayX - 100, behavior: 'smooth' });
  }, [wbsData, timeUnit]);

  // -----------------------------------------------------------------------
  // 렌더
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-gray-400">
        불러오는 중...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <WbsHeader
        timeUnit={timeUnit}
        onTimeUnitChange={setTimeUnit}
        bufferEnabled={bufferEnabled}
        onBufferToggle={handleBufferToggle}
        onScrollToToday={handleScrollToToday}
      />
      <WbsBody
        wbsData={wbsData}
        flattenedRows={flattenedRows}
        timeUnit={timeUnit}
        bufferEnabled={bufferEnabled}
        members={members}
        inlineCreate={inlineCreate}
        onToggle={handleToggle}
        onFieldUpdate={handleFieldUpdate}
        onStartInlineCreate={handleStartInlineCreate}
        onDelete={handleDelete}
        onInlineSubmit={handleInlineSubmit}
        onInlineCancel={() => setInlineCreate(null)}
      />
    </div>
  );
}
